import { useEffect, useMemo, useState } from "react";
import { Loader2, Check, ArrowRight, Calendar as CalendarIcon, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Schedule a private briefing (in-house calendar).
 * Fetches 30-min availability for a chosen business day, posts a booking.
 *
 * Props:
 *  - source: where the booking originated (e.g. "ai-advisor", "contact").
 *  - prefill: { name?, email?, company? } prefill for the form.
 *  - assessmentId: optional advisor assessment id to attach.
 *  - testIdPrefix: testid namespace.
 */
function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextBusinessDays(count = 14) {
  const days = [];
  const cursor = new Date();
  cursor.setDate(cursor.getDate() + 1); // start tomorrow
  while (days.length < count) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) {
      days.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

export default function BookingForm({ source = "contact", prefill = {}, assessmentId, testIdPrefix = "booking" }) {
  const days = useMemo(() => nextBusinessDays(14), []);
  const [date, setDate] = useState(formatDate(days[0]));
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState({ available: [], all_slots: [] });
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({
    name: prefill.name || "",
    email: prefill.email || "",
    company: prefill.company || "",
    phone: "",
    role: prefill.role || "",
    topic: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let alive = true;
    queueMicrotask(() => {
      if (!alive) return;
      setSlotsLoading(true);
      setTime("");
    });
    api
      .get(`/bookings/availability?date=${date}`)
      .then((res) => { if (alive) setSlots(res.data); })
      .catch(() => { if (alive) setSlots({ available: [], all_slots: [] }); })
      .finally(() => { if (alive) setSlotsLoading(false); });
    return () => { alive = false; };
  }, [date]);

  async function submit(e) {
    e.preventDefault();
    if (!time) {
      toast.error("Please select a time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api.post("/bookings", {
        ...form,
        preferred_date: date,
        preferred_time: time,
        timezone: tz,
        source,
        assessment_id: assessmentId,
      });
      setSuccess(res.data);
      toast.success("Briefing requested. We will confirm shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    const dt = new Date(`${success.preferred_date}T${success.preferred_time}:00`);
    const pretty = dt.toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
    });
    return (
      <div className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-10 text-center" data-testid={`${testIdPrefix}-success`}>
        <Check className="mx-auto text-gold" size={36} />
        <div className="font-display text-3xl text-[#001B54] mt-4">Briefing requested.</div>
        <p className="text-[#3F4A6B] mt-4 max-w-md mx-auto leading-relaxed">
          A partner will confirm your private briefing for <span className="text-gold">{pretty}</span>.
          A calendar invitation will follow by email within one business hour.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8 md:p-10 space-y-6"
      data-testid={`${testIdPrefix}-form`}
      aria-label="Schedule a private briefing"
    >
      <div>
        <div className="overline">Schedule a Private Briefing</div>
        <div className="font-display text-2xl text-[#001B54] mt-2">A 30-minute conversation with a partner.</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          required
          placeholder="Full name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid={`${testIdPrefix}-input-name`}
          aria-label="Full name"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
        <input
          required
          type="email"
          placeholder="Business email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid={`${testIdPrefix}-input-email`}
          aria-label="Business email"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
        <input
          placeholder="Company"
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          data-testid={`${testIdPrefix}-input-company`}
          aria-label="Company"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
        <input
          placeholder="Role / title"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid={`${testIdPrefix}-input-role`}
          aria-label="Role"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
        <input
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid={`${testIdPrefix}-input-phone`}
          aria-label="Phone"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
        <input
          placeholder="Topic (e.g. AI Strategy)"
          value={form.topic}
          onChange={(e) => setForm({ ...form, topic: e.target.value })}
          data-testid={`${testIdPrefix}-input-topic`}
          aria-label="Topic"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
        />
      </div>

      <div>
        <div className="overline mb-3 flex items-center gap-2"><CalendarIcon size={12} /> Select a date</div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" role="radiogroup" aria-label="Select a date">
          {days.map((d) => {
            const iso = formatDate(d);
            const active = iso === date;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setDate(iso)}
                role="radio"
                aria-checked={active}
                data-testid={`${testIdPrefix}-date-${iso}`}
                className={`min-w-[88px] px-3 py-3 border text-left transition-colors ${
                  active ? "border-gold bg-gold/[0.08]" : "border-[#001B54]/12 hover:border-gold/40"
                }`}
              >
                <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#6B7390]">
                  {d.toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <div className="font-display text-2xl text-[#001B54] leading-tight mt-0.5">
                  {String(d.getDate()).padStart(2, "0")}
                </div>
                <div className="text-[0.6rem] tracking-[0.18em] uppercase text-[#6B7390] mt-0.5">
                  {d.toLocaleDateString(undefined, { month: "short" })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="overline mb-3 flex items-center gap-2"><Clock size={12} /> Select a 30-minute slot</div>
        {slotsLoading ? (
          <div className="text-[#6B7390] text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading availability…
          </div>
        ) : slots.all_slots.length === 0 ? (
          <div className="text-[#6B7390] text-sm">No slots available for that date.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2" role="radiogroup" aria-label="Select a time slot">
            {slots.all_slots.map((s) => {
              const isAvailable = slots.available.includes(s);
              const active = s === time;
              return (
                <button
                  key={s}
                  type="button"
                  disabled={!isAvailable}
                  onClick={() => isAvailable && setTime(s)}
                  role="radio"
                  aria-checked={active}
                  data-testid={`${testIdPrefix}-slot-${s}`}
                  className={`py-2.5 border text-xs tracking-wide transition-colors ${
                    !isAvailable ? "border-[#001B54]/8 text-[#001B54]/25 line-through cursor-not-allowed" :
                    active ? "border-gold bg-gold text-navy" : "border-[#001B54]/12 text-[#0A1228] hover:border-gold/50"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <textarea
        placeholder="Anything our partner should know in advance (optional)"
        rows={3}
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        data-testid={`${testIdPrefix}-input-notes`}
        aria-label="Notes"
        className="w-full bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]"
      />

      <button
        type="submit"
        disabled={submitting || !time}
        data-testid={`${testIdPrefix}-submit`}
        className="luxury-button w-full justify-center"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Request Briefing <ArrowRight size={14} /></>}
      </button>

      <p className="text-[0.65rem] tracking-wider text-[#6B7390] text-center">
        Briefings are confidential. A partner will confirm by email within one business hour.
      </p>
    </form>
  );
}
